// src/api.js — Central API helper
// All calls go through here so the base URL is defined in one place

const API_BASE = process.env.REACT_APP_API_URL || 'https://runarounddogtown.com/rescue-api/index.php';

function apiUrl(route) {
  return `${API_BASE}?route=${route}`;
}

async function apiFetch(route, options = {}) {
  const token = localStorage.getItem('rescue_token');
  const headers = { 'Content-Type': 'application/json', ...options.headers };
  if (token) headers['Authorization'] = `Bearer ${token}`;

  // Don't set Content-Type for FormData (browser sets it with boundary)
  if (options.body instanceof FormData) {
    delete headers['Content-Type'];
  }

  const res = await fetch(apiUrl(route), { ...options, headers });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Request failed');
  return data;
}

// ── Public API ────────────────────────────────────────────────
export const api = {
  // Dogs
  getDogs:     (params = '') => apiFetch(`api/dogs${params ? '&' + params : ''}`),
  getDog:      (id)          => apiFetch(`api/dogs/${id}`),

  // Events
  getEvents:   ()            => apiFetch('api/events'),

  // Memorial
  getMemorial: ()            => apiFetch('api/memorial'),

  // Settings & SEO
  getSettings: ()            => apiFetch('api/settings'),
  getSeo:      ()            => apiFetch('api/seo'),

  // Contact
  contact:     (data)        => apiFetch('api/contact', { method: 'POST', body: JSON.stringify(data) }),

  // Inquiries (public submit)
  submitInquiry: (data)      => apiFetch('api/inquiries', { method: 'POST', body: JSON.stringify(data) }),

  // Auth
  login:       (email, password) => apiFetch('api/auth/login', {
    method: 'POST',
    body: JSON.stringify({ email, password }),
  }),
  getMe:       ()            => apiFetch('api/auth/me'),
  forgotPassword: (email)    => apiFetch('api/auth/forgot-password', {
    method: 'POST',
    body: JSON.stringify({ email }),
  }),
  resetPassword: (data)      => apiFetch('api/auth/reset-password', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  changePassword: (data)     => apiFetch('api/auth/change-password', {
    method: 'POST',
    body: JSON.stringify(data),
  }),

  // ── Admin API ───────────────────────────────────────────────
  admin: {
    // Dogs
    getAllDogs:    ()         => apiFetch('api/dogs/all'),
    createDog:    (fd)       => apiFetch('api/dogs', { method: 'POST', body: fd }),
    updateDog:    (id, data) => apiFetch(`api/dogs/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    deleteDog:    (id)       => apiFetch(`api/dogs/${id}`, { method: 'DELETE' }),
    uploadPhotos: (id, fd)   => apiFetch(`api/dogs/${id}/photos`, { method: 'POST', body: fd }),
    deletePhoto:  (id, path) => apiFetch(`api/dogs/${id}/photos`, { method: 'DELETE', body: JSON.stringify({ photoPath: path }) }),

    // Events
    getAllEvents:  ()         => apiFetch('api/events/all'),
    createEvent:  (fd)       => apiFetch('api/events', { method: 'POST', body: fd }),
    updateEvent:  (id, fd)   => apiFetch(`api/events/${id}`, { method: 'PUT', body: fd }),
    deleteEvent:  (id)       => apiFetch(`api/events/${id}`, { method: 'DELETE' }),

    // Inquiries
    getInquiries: (params='')=> apiFetch(`api/inquiries${params ? '&' + params : ''}`),
    getInquiry:   (id)       => apiFetch(`api/inquiries/${id}`),
    updateInquiry:(id, data) => apiFetch(`api/inquiries/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
    deleteInquiry:(id)       => apiFetch(`api/inquiries/${id}`, { method: 'DELETE' }),

    // Memorial
    createMemorial:(fd)      => apiFetch('api/memorial', { method: 'POST', body: fd }),
    updateMemorial:(id, fd)  => apiFetch(`api/memorial/${id}`, { method: 'PUT', body: fd }),
    deleteMemorial:(id)      => apiFetch(`api/memorial/${id}`, { method: 'DELETE' }),

    // Users
    getUsers:     ()         => apiFetch('api/users'),
    createUser:   (data)     => apiFetch('api/users', { method: 'POST', body: JSON.stringify(data) }),
    updateUser:   (id, data) => apiFetch(`api/users/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    deleteUser:   (id)       => apiFetch(`api/users/${id}`, { method: 'DELETE' }),

    // SEO
    getSeo:       ()         => apiFetch('api/seo'),
    updateSeo:    (slug, fd) => apiFetch(`api/seo/${slug}`, { method: 'PUT', body: fd }),

    // Settings
    updateSettings:(data)    => apiFetch('api/settings', { method: 'PUT', body: JSON.stringify(data) }),
  },
};

export default api;
